
import { useState, useEffect, useRef } from 'react';

export default function TestimonialsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    const testimonialCards = sectionRef.current?.querySelectorAll('.testimonial-card');
    testimonialCards?.forEach((card, index) => {
      card.classList.add(`delay-${index * 200}`);
      observer.observe(card);
    });

    // Parallax effect
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrolled = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        const parallaxSpeed = scrolled * 20;
        
        const bgElements = sectionRef.current.querySelectorAll('.testimonial-bg');
        bgElements.forEach((element, index) => {
          const speed = (index % 2 === 0 ? 1 : -1) * parallaxSpeed;
          (element as HTMLElement).style.transform = `translateY(${speed}px) rotate(${scrolled * 10}deg)`;
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'E-commerce Owner',
      company: 'Johnson Beauty Co.',
      content: 'The funnel Cofunnels built for us increased our conversion rate by 340%. Our monthly revenue went from $15k to over $50k in just 3 months!',
      image: 'https://readdy.ai/api/search-image?query=Professional%20businesswoman%20with%20blonde%20hair%20in%20modern%20office%20setting%2C%20confident%20smile%2C%20business%20attire%2C%20corporate%20headshot%20style%2C%20clean%20background%2C%20professional%20photography&width=80&height=80&seq=testimonial-sarah&orientation=squarish',
      rating: 5,
      revenue: '+340% Conversion'
    },
    {
      name: 'Michael Chen',
      role: 'SaaS Founder',
      company: 'TechFlow Solutions',
      content: 'Their GoHighLevel integration was seamless. We now have automated follow-ups that convert 28% of our leads. Best investment we\'ve made!',
      image: 'https://readdy.ai/api/search-image?query=Asian%20businessman%20with%20glasses%20in%20modern%20tech%20office%2C%20professional%20smile%2C%20business%20casual%20attire%2C%20confident%20pose%2C%20clean%20corporate%20background%2C%20professional%20photography&width=80&height=80&seq=testimonial-michael&orientation=squarish',
      rating: 5,
      revenue: '28% Lead Conversion'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Course Creator',
      company: 'Digital Marketing Mastery',
      content: 'My course sales funnel is now generating $25k per month on autopilot. The team at Cofunnels knows exactly what they\'re doing!',
      image: 'https://readdy.ai/api/search-image?query=Hispanic%20businesswoman%20with%20long%20dark%20hair%2C%20warm%20smile%2C%20professional%20attire%2C%20modern%20office%20background%2C%20confident%20and%20approachable%2C%20professional%20photography&width=80&height=80&seq=testimonial-emily&orientation=squarish',
      rating: 5,
      revenue: '$25k Monthly'
    },
    {
      name: 'David Thompson',
      role: 'Fitness Coach',
      company: 'Elite Fitness Programs',
      content: 'From zero to $100k in 6 months! Their mobile-optimized funnels capture leads like crazy. 5-star service all the way.',
      image: 'https://readdy.ai/api/search-image?query=Fitness%20professional%20man%20with%20athletic%20build%2C%20confident%20smile%2C%20modern%20gym%20or%20office%20background%2C%20professional%20business%20casual%20attire%2C%20motivational%20presence&width=80&height=80&seq=testimonial-david&orientation=squarish',
      rating: 5,
      revenue: '$100k in 6 Months'
    },
    {
      name: 'Lisa Park',
      role: 'Consultant',
      company: 'Strategic Business Solutions',
      content: 'The conversion optimization they did boosted our client acquisition by 250%. We can\'t keep up with the demand now!',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20businesswoman%20with%20shoulder%20length%20hair%2C%20confident%20expression%2C%20modern%20office%20setting%2C%20business%20suit%2C%20executive%20presence%2C%20clean%20background&width=80&height=80&seq=testimonial-lisa&orientation=squarish',
      rating: 5,
      revenue: '+250% Clients'
    },
    {
      name: 'Robert Kim',
      role: 'Agency Owner',
      company: 'Digital Growth Agency',
      content: 'Their done-for-you funnels helped us scale to 7 figures. The ROI has been incredible - over 800% return on investment!',
      image: 'https://readdy.ai/api/search-image?query=Korean%20businessman%20in%20modern%20office%2C%20professional%20smile%2C%20business%20suit%2C%20confident%20leadership%20pose%2C%20contemporary%20workspace%20background%2C%20executive%20photography&width=80&height=80&seq=testimonial-robert&orientation=squarish',
      rating: 5,
      revenue: '800% ROI'
    }
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-br from-blue-50 to-purple-50 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="testimonial-bg absolute top-20 left-10 w-32 h-32 bg-blue-200 rounded-full opacity-20"></div>
        <div className="testimonial-bg absolute bottom-20 right-10 w-40 h-40 bg-purple-200 rounded-full opacity-20"></div>
        <div className="testimonial-bg absolute top-1/2 left-1/4 w-24 h-24 bg-yellow-200 rounded-full opacity-20"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            What Our Clients Say
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real success stories from businesses that transformed their revenue with our funnels
          </p>
        </div>

        {/* Main testimonial carousel */}
        <div className="relative mb-12">
          <div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-12 mx-auto max-w-4xl transform hover:scale-105 transition-all duration-500">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-2xl text-yellow-400 animate-pulse" style={{ animationDelay: `${i * 100}ms` }}></i>
                ))}
              </div>
              
              <blockquote className="text-xl sm:text-2xl text-gray-700 italic mb-8 leading-relaxed">
                "{testimonials[currentIndex].content}"
              </blockquote>
              
              <div className="flex items-center justify-center mb-6">
                <img 
                  src={testimonials[currentIndex].image}
                  alt={testimonials[currentIndex].name}
                  className="w-16 h-16 rounded-full mr-4 object-cover border-4 border-blue-100 shadow-lg"
                />
                <div className="text-left">
                  <h4 className="text-lg font-semibold text-gray-900">{testimonials[currentIndex].name}</h4>
                  <p className="text-gray-600">{testimonials[currentIndex].role}</p>
                  <p className="text-sm text-blue-600 font-medium">{testimonials[currentIndex].company}</p>
                </div>
              </div>
              
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-100 to-blue-100 rounded-full">
                <i className="ri-trending-up-line text-green-600 mr-2 animate-bounce"></i>
                <span className="font-semibold text-green-800">{testimonials[currentIndex].revenue}</span>
              </div>
            </div>
          </div>
          
          {/* Navigation arrows */}
          <button 
            onClick={prevTestimonial}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-50 transition-all duration-300 hover:scale-110"
          >
            <i className="ri-arrow-left-line text-xl text-gray-600 hover:text-blue-600"></i>
          </button>
          <button 
            onClick={nextTestimonial}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-50 transition-all duration-300 hover:scale-110"
          >
            <i className="ri-arrow-right-line text-xl text-gray-600 hover:text-blue-600"></i>
          </button>
        </div>

        {/* Dots indicator */}
        <div className="flex justify-center space-x-2 mb-12">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-blue-600 w-8' 
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>

        {/* Testimonial grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.slice(0, 6).map((testimonial, index) => (
            <div 
              key={index}
              className="testimonial-card bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-500 cursor-pointer opacity-0 translate-y-8 hover:translate-y-[-4px]"
              onClick={() => goToSlide(index)}
            >
              <div className="flex mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-600 text-sm mb-4 italic">"{testimonial.content.substring(0, 100)}..."</p>
              <div className="flex items-center">
                <img 
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-10 h-10 rounded-full mr-3 object-cover"
                />
                <div>
                  <h5 className="font-medium text-gray-900 text-sm">{testimonial.name}</h5>
                  <p className="text-xs text-gray-500">{testimonial.company}</p>
                </div>
              </div>
              <div className="mt-3 text-xs font-semibold text-green-600">
                {testimonial.revenue}
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
        .testimonial-card {
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }
        .delay-0 { transition-delay: 0ms; }
        .delay-200 { transition-delay: 200ms; }
        .delay-400 { transition-delay: 400ms; }
        .delay-600 { transition-delay: 600ms; }
        .delay-800 { transition-delay: 800ms; }
        .delay-1000 { transition-delay: 1000ms; }
      `}</style>
    </section>
  );
}
